/**
 * Iter 67co → Iter 69 — City-level cluster summary on the Browse results page.
 *
 * Originally a Balkan-country chip strip; refactored to one chip per
 * Albanian city found in the current `cars` payload so renters can
 * jump straight to the city they care about (Tirana, Vlora, Saranda…).
 *
 * Sits above the results count and renders one chip per Albanian city,
 * with a live car-count badge. Clicking sets `?city=X` in the URL and
 * the parent narrows the result set client-side. Clicking the same chip
 * again (or the "All cities" chip) clears the filter.
 *
 * Visible in BOTH list and map views.
 *
 * Derives city from `car.location` ("City, Country" pattern) — only
 * cars whose country resolves to "Albania" are bucketed. Cars listed
 * outside Albania aren't dropped from the result set itself, only their
 * chip is hidden so the strip stays focused on the platform's home market.
 */
import { useMemo } from "react";
import { MapPin } from "lucide-react";

// Light alias map so different spellings of the same Albanian city collapse
// into a single chip (e.g. "Vlorë" + "Vlora" → "Vlora"). Anything not in
// this map renders verbatim from the listing data.
const CITY_ALIASES = {
  "Tiranë": "Tirana",
  "Vlorë": "Vlora",
  "Sarandë": "Saranda",
  "Shkodër": "Shkodra",
  "Gjirokastër": "Gjirokastra",
  "Korçë": "Korca",
  "Lezhë": "Lezha",
  "Krujë": "Kruja",
  "Himarë": "Himara",
  "Durrës": "Durrës",  // canonical form retained
};

const _cityFromLocation = (loc) => {
  if (!loc || typeof loc !== "string") return null;
  const idx = loc.lastIndexOf(",");
  if (idx === -1) return null;
  const city = loc.slice(0, idx).trim();
  return CITY_ALIASES[city] || city;
};

const _countryFromLocation = (loc) => {
  if (!loc || typeof loc !== "string") return null;
  const idx = loc.lastIndexOf(",");
  if (idx === -1) return null;
  return loc.slice(idx + 1).trim();
};

export default function BrowseCountrySummary({ cars, selectedCity, onSelect }) {
  // Aggregate counts by Albanian city only.
  const buckets = useMemo(() => {
    const m = new Map();
    for (const c of cars || []) {
      const country = _countryFromLocation(c.location);
      if (country !== "Albania") continue;
      const city = _cityFromLocation(c.location);
      if (!city) continue;
      m.set(city, (m.get(city) || 0) + 1);
    }
    return Array.from(m, ([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [cars]);

  if (buckets.length === 0) return null;
  const total = buckets.reduce((acc, b) => acc + b.count, 0);

  return (
    <div className="mb-3" data-testid="browse-city-summary">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="h-3.5 w-3.5 text-neutral-500" />
        <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-neutral-500">
          {selectedCity ? `Filtered to ${selectedCity}` : "Cars by Albanian city in your results"}
        </span>
      </div>
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
        <Chip
          active={!selectedCity}
          onClick={() => onSelect(null)}
          testId="browse-city-chip-all"
        >
          <span className="text-base">🇦🇱</span>
          <span>All cities</span>
          <span className="text-[10px] tabular-nums opacity-80">{total}</span>
        </Chip>
        {buckets.map((b) => (
          <Chip
            key={b.name}
            active={selectedCity === b.name}
            onClick={() => onSelect(b.name)}
            testId={`browse-city-chip-${b.name}`}
          >
            <span className="text-base">📍</span>
            <span>{b.name}</span>
            <span className="text-[10px] tabular-nums opacity-80">{b.count}</span>
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
        active
          ? "bg-neutral-900 text-white border-neutral-900"
          : "bg-white border-border text-neutral-700 hover:border-neutral-400"
      }`}
    >
      {children}
    </button>
  );
}

// Expose the alias normaliser so Browse.jsx can apply the same logic when
// filtering visibleCars by city.
export { _cityFromLocation, _countryFromLocation };
